// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { AuthState } from "../store/auth";
import CheckEmail from "components/Login/VerifyEmail";
import Cleanup from "components/Cleanup/Cleanup";
import ComingSoon from "../components/ComingSoon/ComingSoon";
import { CustomerConfig } from "utils/ConfigTypes";
import ExpertReadPanel from "components/ExpertRead/ExpertReadPanel";
import Home from "../components/Home/Home";
import Login from "../components/Login/Login";
import NewTestRun from "components/TestRun/NewTestRun";
import PasswordForget from "../components/Login/PasswordForget";
import ProfileCreation from "components/ProfileSelection/ProfileCreation";
import ProfileSelection from "../components/ProfileSelection/ProfileSelection";
import RDTInterpreterPanel from "components/RDTInterpreter/RDTInterpreterPanel";
import SignUp from "../components/Login/SignUp";
import TermsAndConditions from "../components/TermsAndConditions/TermsAndConditions";
import TestResult from "components/TestResult/TestResult";
import TestRunHome from "../components/TestRun/TestRunHome";
import TestRunSteps from "../components/TestRun/TestRunSteps";
import UserNotInOrg from "components/Login/UserNotInOrg";
import { lazy } from "react";

const EditorHome = lazy(() => import("components/Editor/EditorHome"));
const Editor = lazy(() => import("components/Editor/Editor"));
const AdminPanel = lazy(() => import("components/Admin/Admin"));

export interface seoDefinition {
  title: string;
  description: string;
}
export interface DashboardRoute {
  name: string;
  path: string;
  exact: boolean;
  // Does this live under /:customerPrefix/? Defaults to true
  customerSpecific?: boolean;
  // Limit this route to the specified customers
  customers?: string[];
  // Should we hide the nav bar on this page.
  hideNavBar?: true;
  // should it have a tab in the nav bar?
  renderNavTab: (params: {
    authState: AuthState;
    customerConfig?: CustomerConfig;
  }) => boolean;
  navTitle?: string;
  // auth access to the page.
  pageAccess: keyof typeof PAGE_ACCESS_OPTIONS;
  seo: seoDefinition;
  component: React.ComponentClass<any, any> | React.FunctionComponent<any>;
}

function never(): boolean {
  return false;
}

function always(): boolean {
  return true;
}

function loggedInVerified(params: {
  authState: AuthState;
  customerConfig?: CustomerConfig;
}): boolean {
  const { authState } = params;
  const auth = authState.auth;
  return (
    !!(auth && auth.emailVerified) &&
    !!(authState?.auth?.orgId === params.customerConfig?.id)
  );
}

function loggedInVerifiedAnyOrg(params: {
  authState: AuthState;
  customerConfig?: CustomerConfig;
}): boolean {
  const { authState } = params;
  const auth = authState.auth;
  return !!(auth && auth.emailVerified);
}

function loggedInUnverified(params: {
  authState: AuthState;
  customerConfig?: CustomerConfig;
}): boolean {
  const { authState } = params;
  const auth = authState?.auth;
  return !!auth && !auth.emailVerified;
}

function loggedOut(params: { authState: AuthState }) {
  const { authState } = params;
  return !authState || !!authState.auth?.isGuest;
}

function loggedInVerifiedOrGuest(params: {
  authState: AuthState;
  customerConfig?: CustomerConfig;
}): boolean {
  const { customerConfig } = params;
  return customerConfig?.allowGuestRuns ? always() : loggedInVerified(params);
}

export const PAGE_ACCESS_OPTIONS = {
  never,
  always,
  loggedInVerified,
  loggedInVerifiedAnyOrg,
  loggedInVerifiedOrGuest,
  loggedInUnverified,
  loggedOut,
};

export const ROUTE_DEFINITIONS: { [key: string]: DashboardRoute } = {
  ADMIN: {
    name: "ADMIN",
    path: "/admin",
    exact: true,
    renderNavTab: never,
    navTitle: "Admin Tools",
    pageAccess: "loggedInVerified",
    seo: {
      title: "Admin Tools",
      description: "Access administrative tools",
    },
    component: AdminPanel,
  },
  EDITOR: {
    name: "EDITOR",
    path: "/editor",
    exact: true,
    renderNavTab: never,
    navTitle: "Test Editor",
    pageAccess: "loggedInVerified",
    seo: {
      title: "Test Editor",
      description: "Edit your test flow",
    },
    component: EditorHome,
  },
  EDITOR_EDIT_STEP: {
    name: "EDITOR_EDIT_STEP",
    path: "/editor/:testRunUID/:step",
    exact: true,
    renderNavTab: never,
    navTitle: "Test Editor",
    pageAccess: "loggedInVerified",
    seo: {
      title: "Test Editor",
      description: "Edit your test flow",
    },
    component: Editor,
  },
  HOME: {
    name: "HOME",
    path: "/home",
    exact: true,
    renderNavTab: never,
    navTitle: "Home",
    pageAccess: "never",
    seo: {
      title: "Home",
      description: "Your Homepage for the OpenRDT Dashboard",
    },
    component: Home,
  },
  LOGIN: {
    name: "LOGIN",
    path: "/login",
    exact: true,
    hideNavBar: true,
    renderNavTab: loggedOut,
    navTitle: "login",
    pageAccess: "loggedOut",
    seo: {
      title: "Login",
      description: "Login Page for the OpenRDT Dashboard",
    },
    component: Login,
  },
  SIGNUP: {
    name: "SIGNUP",
    path: "/signup",
    exact: true,
    hideNavBar: true,
    renderNavTab: loggedOut,
    navTitle: "signup",
    pageAccess: "loggedOut",
    seo: {
      title: "Sign Up",
      description: "Sign Up Page for the OpenRDT Dashboard",
    },
    component: SignUp,
  },
  VERIFYEMAIL: {
    name: "VERIFYEMAIL",
    path: "/verifyemail",
    exact: true,
    hideNavBar: true,
    renderNavTab: never,
    pageAccess: "loggedInUnverified",
    seo: {
      title: "Check Your Email",
      description: "Page to alert user to verify their email.",
    },
    component: CheckEmail,
  },
  FORGOTPASSWORD: {
    name: "FORGOTPASSWORD",
    path: "/forgotpassword",
    exact: true,
    hideNavBar: true,
    renderNavTab: never,
    pageAccess: "loggedOut",
    seo: {
      title: "Password Recovery",
      description: "Password Recovery Page for the OpenRDT Dashboard",
    },
    component: PasswordForget,
  },
  NOTINORG: {
    name: "NOTINORG",
    path: "/notinorg",
    exact: true,
    hideNavBar: true,
    renderNavTab: never,
    pageAccess: "loggedInVerifiedAnyOrg",
    seo: {
      title: "Access not allowed",
      description: "User Not Allowed Page for the OpenRDT Dashboard",
    },
    component: UserNotInOrg,
  },
  ORDERTEST: {
    name: "ORDERTEST",
    path: "/ordertest",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerified",
    seo: {
      title: "Order a Test Kit",
      description: "Order Page for OpenRDT Test kits",
    },
    component: ComingSoon,
  },
  TESTRUNHOME: {
    name: "TESTRUNHOME",
    path: "/testrunhome",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerified",
    seo: {
      title: "Perform a Test",
      description: "Entry Page for OpenRDT Testing Flow",
    },
    component: TestRunHome,
  },
  MOREINFO: {
    name: "MOREINFO",
    path: "/moreinfo",
    exact: true,
    renderNavTab: never,
    pageAccess: "always",
    seo: {
      title: "What is OpenRDT",
      description: "Info Page for OpenRDT",
    },
    component: ComingSoon,
  },
  TESTCAMERA: {
    name: "TESTCAMERA",
    path: "/testcamera",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerified",
    seo: {
      title: "Camera Test",
      description: "Test your device's ability to take a picture of the kit",
    },
    component: ComingSoon,
  },
  IDINFO: {
    name: "IDINFO",
    path: "/idinfo",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerified",
    seo: {
      title: "ID Info",
      description: "Why do you need to provide an ID to perform a RDT",
    },
    component: ComingSoon,
  },
  CHOOSEPROFILE: {
    name: "CHOOSEPROFILE",
    path: "/chooseprofile",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerified",
    seo: {
      title: "Select Someone",
      description: "Person selection for OpenRDT testing",
    },
    component: ProfileSelection,
  },
  CREATEPROFILE: {
    name: "CREATEPROFILE",
    path: "/createProfile",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerified",
    seo: {
      title: "Create Profile",
      description: "Create a person profile for OpenRDT testing",
    },
    component: ProfileCreation,
  },
  PERFORMTEST: {
    name: "PERFORMTEST",
    path: "/testrunsteps/:testRunUID/:step/:page?",
    exact: false,
    renderNavTab: never,
    pageAccess: "loggedInVerifiedOrGuest",
    seo: {
      title: "Perform a test",
      description: "Step by step flow to perform a test.",
    },
    component: TestRunSteps,
  },
  TESTRESULT: {
    name: "TESTRESULT",
    path: "/testresult/:testRunUID",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerifiedOrGuest",
    seo: {
      title: "Test Result",
      description: "Page with the OpenRDT test result.",
    },
    component: TestResult,
  },
  TERMSANDCONDITIONS: {
    name: "TERMSANDCONDITIONS",
    path: "/termsandconditions",
    exact: true,
    renderNavTab: never,
    pageAccess: "always",
    seo: {
      title: "Terms and Conditions",
      description: "Page with the OpenRDT terms and conditions.",
    },
    component: TermsAndConditions,
  },
  CLEANUP: {
    name: "CLEANUP",
    path: "/cleanup",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerifiedOrGuest",
    seo: {
      title: "Cleanup",
      description: "Test clean up instructions.",
    },
    component: Cleanup,
  },
  NEWTESTRUN: {
    name: "NEWTESTRUN",
    path: "/newtestrun",
    exact: true,
    renderNavTab: never,
    pageAccess: "loggedInVerifiedOrGuest",
    seo: {
      title: "New Test Run",
      description: "Start a new test run.",
    },
    component: NewTestRun,
  },
  EXPERTREAD: {
    name: "EXPERTREAD",
    path: "/expertread/:project?/:uid?",
    exact: false,
    renderNavTab: never,
    pageAccess: "loggedInVerified",
    customers: ["expertread"],
    seo: {
      title: "Expert Read Tool",
      description: "View RDT photos and record interpretations",
    },
    component: ExpertReadPanel,
  },
  INTERPRETPHOTO: {
    name: "INTERPRETPHOTO",
    path: "/interpret",
    exact: true,
    renderNavTab: never,
    hideNavBar: true,
    pageAccess: "always",
    customerSpecific: false,
    seo: {
      title: "Audere Photo Interpreter Demo",
      description:
        "Upload a photo and see the CV interpretation results",
    },
    component: RDTInterpreterPanel,
  },
};

ROUTE_DEFINITIONS.LANDING = {
  ...ROUTE_DEFINITIONS.LOGIN,
  path: "/",
  customerSpecific: false,
  renderNavTab: never,
};

export function getPath(
  route: DashboardRoute,
  options?: {
    customerPrefix?: string;
    params?: {
      [paramName: string]: string;
    };
    asRoute?: boolean;
  }
) {
  options = options || {};
  let { customerPrefix } = options;
  const { params, asRoute } = options;

  if (route.customerSpecific === false) {
    return route.path;
  }
  if (!customerPrefix) {
    const match = window.location.pathname.match(/^\/([^/]*)/);
    if (match) {
      customerPrefix = match[1];
    } else {
      customerPrefix = "nhs"; // TODO(ram): put this in a config somehwere
    }
  }
  let path = `/${customerPrefix}${route.path}`;
  if (params) {
    for (const paramName in params) {
      path = path.replace(paramName, params[paramName]);
    }
  }

  if (!asRoute) {
    // Remove unset optional params
    path = path.replace(/:[^/?]*\?\/?/g, "");
  }

  return path;
}

export const ROUTES: Array<DashboardRoute> = Object.values(ROUTE_DEFINITIONS);
